import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface ChangeItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  type: 'addition' | 'deduction' | 'modification';
}

export interface ChangeImpact {
  originalBudget: number;
  newBudget: number;
  impact: number;
  impactPercentage: number;
  itemsSummary: {
    additions: number;
    deductions: number;
    modifications: number;
  };
  timelineImpact: number;
  riskLevel: 'low' | 'medium' | 'high';
  recommendation: 'approve' | 'reject' | 'review';
}

export const changeManagementService = {
  calculateImpact: (items: ChangeItem[], originalBudget: number): ChangeImpact => {
    const totalAddition = items
      .filter(item => item.type === 'addition')
      .reduce((sum, item) => sum + item.total, 0);
    const totalDeduction = items
      .filter(item => item.type === 'deduction')
      .reduce((sum, item) => sum + item.total, 0);
    
    const netImpact = totalAddition - totalDeduction;
    const newBudget = originalBudget + netImpact;
    const impactPercentage = (netImpact / originalBudget) * 100;

    return {
      originalBudget,
      newBudget,
      impact: netImpact,
      impactPercentage,
      itemsSummary: {
        additions: totalAddition,
        deductions: totalDeduction,
        modifications: 0,
      },
      timelineImpact: 0,
      riskLevel: Math.abs(impactPercentage) > 10 ? 'high' : 'medium',
      recommendation: Math.abs(impactPercentage) > 15 ? 'review' : 'approve',
    };
  },

  createChangeOrder: async (data: any) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getChangeOrders: async (projectId: string) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders?projectId=${projectId}`);
  },

  getChangeOrder: async (id: string) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders/${id}`);
  },

  updateChangeOrder: async (id: string, data: any) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteChangeOrder: async (id: string) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders/${id}`, {
      method: 'DELETE',
    });
  },

  approveChangeOrder: async (id: string) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders/${id}/approve`, {
      method: 'POST',
    });
  },

  rejectChangeOrder: async (id: string, reason: string) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  executeChangeOrder: async (id: string) => {
    return authService.fetchWithAuth(`${API_URL}/api/change-orders/${id}/execute`, {
      method: 'POST',
    });
  },
};